export type TenantTier = "basic" | "business" | "enterprise";

export type TierConfig = {
  pricePerSeat: number; // in cents
  minSeats: number;
  maxAdmins: number;
  apiCalls: number;
};

export const TIER_CONFIG: Record<TenantTier, TierConfig> = {
  basic: {
    pricePerSeat: 10000, // €100 in cents
    minSeats: 3,
    maxAdmins: 1,
    apiCalls: 500,
  },
  business: {
    pricePerSeat: 13000, // €130 in cents
    minSeats: 10,
    maxAdmins: 5,
    apiCalls: 3000,
  },
  enterprise: {
    pricePerSeat: 0, // Contact sales
    minSeats: 25,
    maxAdmins: Number.POSITIVE_INFINITY,
    apiCalls: Number.POSITIVE_INFINITY,
  },
} as const;

/**
 * Calculate monthly subscription amount in cents
 */
export function calculateMonthlyAmount(tier: TenantTier, seats: number): number {
  const config = TIER_CONFIG[tier];
  const actualSeats = Math.max(seats, config.minSeats);
  return config.pricePerSeat * actualSeats;
}

/**
 * Format amount from cents to display string (e.g., "€100.00")
 */
export function formatAmount(cents: number, currency = "EUR"): string {
  return new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency,
  }).format(cents / 100);
}

/**
 * Calculate proration when changing plans mid-cycle
 */
export function calculateProration(
  currentTier: TenantTier,
  currentSeats: number,
  newTier: TenantTier,
  newSeats: number,
  daysRemaining: number,
  totalDays: number,
): { immediate: number; nextMonth: number } {
  const currentMonthly = calculateMonthlyAmount(currentTier, currentSeats);
  const newMonthly = calculateMonthlyAmount(newTier, newSeats);

  // Guard against division by zero
  const ratio = totalDays > 0 ? daysRemaining / totalDays : 0;
  const creditRemaining = Math.round(currentMonthly * ratio);
  const chargeRemaining = Math.round(newMonthly * ratio);

  return {
    immediate: Math.max(0, chargeRemaining - creditRemaining),
    nextMonth: newMonthly,
  };
}

/**
 * Validate seat count for a tier
 */
export function validateSeats(
  tier: TenantTier,
  seats: number,
): { valid: boolean; minSeats: number } {
  const config = TIER_CONFIG[tier];
  return {
    valid: seats >= config.minSeats,
    minSeats: config.minSeats,
  };
}
